import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  BuildingOfficeIcon,
  CalendarIcon,
  CheckIcon,
  PencilIcon,
  ChartPieIcon,
  CalculatorIcon,
  TrophyIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { checkApi } from '../Authentication/AxiosInstance';
import EmailChangeModal from './StudentDashboard/EmailChangeModal';
import { useUser } from '../store/UserContext';
import SDProfile from './StudentDashboard/SDProfile';
import SDExam from './StudentDashboard/SDExam';
import SDPrefColleges from './StudentDashboard/SDPrefColleges';
import SDPrefBranches from './StudentDashboard/SDPrefBranches';
import SDExamSchedules from './StudentDashboard/SDExamSchedules';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [candidateData, setCandidateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);
  const [refreshExamList, setRefreshExamList] = useState(true);
  const [refreshPrefColleges, setRefreshPrefColleges] = useState(true);
  const [refreshPrefBranches, setRefreshPrefBranches] = useState(true);
  
  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
  console.log(API_ENDPOINT);

  const fetchCandidateData = async () => {
    try {
      setLoading(true);
      setError(null);
      // First, try to get the candidate profile directly from my_profile endpoint
      try {
        const profileResponse = await checkApi.get('/candidates/candidate-details/my_profile/');
        setCandidateData(profileResponse.data);
      } catch (profileError) {
        // If my_profile fails, try to get from the list endpoint
        const listResponse = await checkApi.get('/candidates/candidate-details/');
        if (Array.isArray(listResponse.data) && listResponse.data.length > 0) {
          setCandidateData(listResponse.data[0]);
        } else {
          throw new Error('No candidate profile found');
        }
      }
    } catch (err) {
      if (err.message.includes('401')) {
        setError('Authentication required. Please log in again.');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (err.message.includes('404')) {
        setError('Candidate profile not found. Please complete your profile setup.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Authentication check
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  // Fetch candidate data when user is authenticated
  useEffect(() => {
    if (user) {
      fetchCandidateData();
    }
  }, [user]);

  // Don't render anything if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Redirecting to login...</h2>
        </div>
      </div>
    );
  }

  // Profile photo URL and fallback initial
  const profilePhotoUrl = candidateData?.candidate_photo_url 
    || candidateData?.profile_photo_url 
    || candidateData?.avatar_url 
    || candidateData?.photo_url 
    || '';
  const userInitial = (candidateData?.candidate_firstname || candidateData?.candidate_name || '?').trim().charAt(0).toUpperCase();

  const handleSaveProfile = async (profileData) => {
    try {
      console.log('DEBUG: Original profileData:', profileData);
      // Map frontend field names to backend field names
      const mappedData = {
        section_type: 'profile',
        candidate_mobile: profileData.candidate_mobile,
        candidate_domicile: profileData.candidate_domicile,
        candidate_hsc_district: profileData.candidate_hsc_district,
        candidate_home_university: profileData.candidate_home_university,
        candidate_willing_management_quota: profileData.candidate_management_quota,
      };
      console.log('DEBUG: Mapped data being sent:', mappedData);
      
      const response = await checkApi.put('/candidates/candidate-details/update_profile/', mappedData);
      setCandidateData(response.data.updated_data);
      return response.data.updated_data;
    } catch (error) {
      console.error('Error saving profile:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading your dashboard...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchCandidateData}
            className="bg-primary-600 text-primary-800 px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!candidateData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Profile Found</h2>
          <p className="text-gray-600 mb-6">Please complete your profile setup.</p>
        </div>
      </div>
    );
  }

  console.log('candidateData:', candidateData);
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Profile Summary Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-row gap-4 items-center space-x-4 mb-8">
                  <div className="flex items-center space-x-4">
                    {/* Profile Image */}
                        {profilePhotoUrl ? (
                          <img
                            src={profilePhotoUrl}
                            alt="Profile"
                            className="rounded-full object-cover w-16 h-16"
                            width={64}
                            height={64}
                            loading="lazy"
                          />
                        ) : (
                          <div
                            className="flex items-center justify-center rounded-full w-16 h-16
                            text-white font-bold text-xl"
                            style={{ backgroundColor: candidateData?.user?.usercolorhex || '#6B7280' }}
                          >
                            {userInitial}
                          </div>
                        )}
                  </div>
                  <div className="flex flex-col items-start">
                      <div className="flex items-center ">
                        <h1 className="text-xl font-bold text-gray-900">Hi {candidateData?.candidate_firstname}!</h1>
                      </div>
                      <div className="flex items-start">
                        <h1 className="text-base  text-blue-900">@{candidateData?.candidate_name}</h1>
                      </div>
                      <div className="flex items-start gap-4">
                          <h1 className="text-sm font-bold text-gray-900">{candidateData?.candidate_emailaddress}</h1>
                          <button className="flex items-start space-x-2 p-2 bg-primary-600 text-white rounded-full 
                          hover:bg-primary-700 transition-colors cursor-pointer"
                              onClick={() => setShowEmailChange(true)}>
                              <PencilIcon className="w-2 h-2"/>
                          </button>
                      </div>
                  </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Main Steps */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Step 1: Profile Setup */}
                    <SDProfile candidateData={candidateData} onSaveProfile={handleSaveProfile}/>

                    {/* Step 2: Add Exams */}
                    <SDExam candidateId={candidateData?.id} setRefreshExamList={setRefreshExamList} refreshExamList={refreshExamList}/>

                    {/* Step 3: Add Exams Schedules */}
                    <SDExamSchedules candidateId={candidateData?.id} refreshExamList={refreshExamList} setRefreshExamList={setRefreshExamList}/>

                    {/* Step 3: Shortlist Colleges */}
                    {<SDPrefColleges candidateId={candidateData?.id} onUpdated={() => setRefreshPrefColleges(v => !v)} />}
                    {/* Step 4: Shortlist Branches */}
                    <SDPrefBranches candidateData={candidateData} onSaveProfile={handleSaveProfile} onProfileChange={() => setRefreshPrefBranches(v => !v)} />


                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-orange-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">3</span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">Preferences</h3>
                        </div>
                        <button 
                          className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white 
                          rounded-lg hover:bg-orange-700 transition-colors">
                          <PencilIcon className="w-4 h-4" />
                          <span>Edit Preferences</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <h4 className="font-medium text-gray-900 mb-3">Preferred Branches (0/6)</h4>
                          <div className="text-center py-4 bg-gray-50 rounded-lg">
                            <AcademicCapIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600 text-sm">No branches selected</p>
                          </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  </div>
                          
                    {/* Step 5: Prediction & Insights */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">5</span>
                            </div>
                          <h3 className="text-lg font-semibold text-gray-900">Admission Chances</h3>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-5 w-5 text-yellow-600" />
                            <span className="text-sm text-yellow-600 font-medium">Pending</span>
                          </div>
                        </div>
                        <button className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                          <EyeIcon className="w-4 h-4" />
                          <span>See More</span>
                        </button>
                      </div>
                      <div className="text-center py-8">
                        <ChartPieIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Complete your profile first</h4>
                        <p className="text-gray-600">Add exams and preferences to get admission predictions</p>
                      </div>
                    </div>

                    {/* Step 6: CAP Rounds */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-teal-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">6</span>
                            </div>
                          <h3 className="text-lg font-semibold text-gray-900">Seat Allotment Tracker</h3>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-5 w-5 text-yellow-600" />
                            <span className="text-sm text-yellow-600 font-medium">Not Started</span>
                          </div>
                        </div>
                        <button className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
                          <PencilIcon className="w-4 h-4" />
                          <span>Update Allotment</span>
                          </button>
                        </div>
                      <div className="text-center py-8">
                        <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">CAP rounds not started</h4>
                        <p className="text-gray-600">Track your seat allotment status here once rounds begin</p>
                      </div>
                    </div>

                    {/* Step 7: Finalise Admission */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="h-8 w-8 bg-emerald-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-bold">7</span>
                  </div>
                          <h3 className="text-lg font-semibold text-gray-900">Final Admission Status</h3>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="h-5 w-5 text-yellow-600" />
                            <span className="text-sm text-yellow-600 font-medium">Pending</span>
                </div>
              </div>
                      <button className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                        <CheckIcon className="w-4 h-4" />
                        <span>Mark as Finalised</span>
                    </button>
                  </div>
                    <div className="text-center py-8">
                      <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No final admission yet</h4>
                      <p className="text-gray-600">Complete the admission process to mark your final choice</p>
                    </div>
                    </div>
                </div>

                {/* Right Column - Prediction Widget & Admission Lounge */}
                <div className="space-y-6">
                  {/* Prediction Widget */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <CalculatorIcon className="h-6 w-6 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Prediction Widget</h3>
                      </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ask a question</label>
                        <textarea
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          rows="3"
                          placeholder="e.g., What are my chances at IIT Bombay for Computer Science?"
                        ></textarea>
                      </div>
                      <button className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors">
                        Get Prediction
                      </button>
                    </div>
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Complete your profile and add exam results to get personalized predictions.</p>
                    </div>
                  </div>

                  {/* Admission Lounge */}
                  <div className="bg-orange-50 rounded-xl p-6 shadow-sm border border-orange-200">
                    <div className="flex items-center space-x-3 mb-4">
                      <ChatBubbleLeftRightIcon className="h-6 w-6 text-orange-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Admission Lounge</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <p className="font-medium text-gray-900 text-sm">Which branch is better at VJTI?</p>
                        <p className="text-xs text-gray-600 mt-1">15 replies • 2 hours ago</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <p className="font-medium text-gray-900 text-sm">IIT Bombay vs IIT Delhi</p>
                        <p className="text-xs text-gray-600 mt-1">8 replies • 4 hours ago</p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-orange-200">
                        <p className="font-medium text-gray-900 text-sm">CAP Round 1 Strategy</p>
                        <p className="text-xs text-gray-600 mt-1">12 replies • 6 hours ago</p>
                      </div>
                    </div>
                    <button className="w-full mt-4 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center">
                      <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                      <span>Join Discussion</span>
                    </button>
                  </div>
                </div>
              </div>
          </div>
      </div>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={showEmailChange}
        onClose={() => setShowEmailChange(false)}
        currentEmail={candidateData?.candidate_emailaddress}
        onEmailChanged={() => {
          setEmailChangeSuccess(true);
          // Optionally refresh candidate data
          fetchCandidateData();
        }}
      />

      {/* Success Notification */}
      {emailChangeSuccess && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg z-50">
          <div className="flex items-center space-x-3">
            <CheckIcon className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">Email Change Initiated</p>
              <p className="text-xs text-green-600">Check your new email for verification link</p>
            </div>
            <button
              onClick={() => setEmailChangeSuccess(false)}
              className="text-green-600 hover:text-green-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentDashboard;